import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../../models/Logging';
import {
	ICreateOAuthClientRequest,
	invalidateGetOAuthClients,
	usePostOAuthClientMutation,
} from '../../../../../queries';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../components-v2/Dialog';
import { Field, FieldGroup, FieldLabel } from '../../../../components-v2/Field';
import { Input } from '../../../../components-v2/Input';
import { Label } from '../../../../components-v2/Label';
import { Pill } from '../../../../components-v2/Pill';
import { TextArea } from '../../../../components-v2/TextArea';
import { COMING_SOON_SCOPES, PUBLIC_API_SCOPE_VALUES } from '../constants';
import { MAX_REDIRECT_URIS, validateCreateOAuthClient } from '../validation';
import { styleSheet } from './styles';

interface IProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export const CreateOAuthClientDialog = ({ open, onOpenChange }: IProps) => {
	const { logApiError } = useEventLogging('CreateOAuthClientDialog');

	const [name, setName] = React.useState('');
	const [description, setDescription] = React.useState('');
	const [redirectUris, setRedirectUris] = React.useState<string[]>(['']);
	const [scopes, setScopes] = React.useState<string[]>([]);
	const [error, setError] = React.useState<string | null>(null);

	// Reset the form each time the dialog is opened so a prior attempt's state never leaks in.
	React.useEffect(() => {
		if (open) {
			setName('');
			setDescription('');
			setRedirectUris(['']);
			setScopes([]);
			setError(null);
		}
	}, [open]);

	const createOAuthClient = usePostOAuthClientMutation({
		onSuccess: () => {
			invalidateGetOAuthClients();
			toast.success('OAuth client has been created');
			onOpenChange(false);
		},
		onError: (err: Api.IOperationResultNoValue) => {
			setError(err.systemMessage || 'Failed to create OAuth client');
			logApiError('CreateOAuthClient-Error', err);
		},
	});

	const updateRedirectUri = (index: number, value: string) => {
		setRedirectUris(prev => prev.map((uri, i) => (i === index ? value : uri)));
	};

	const addRedirectUri = () => {
		setRedirectUris(prev => (prev.length < MAX_REDIRECT_URIS ? [...prev, ''] : prev));
	};

	const removeRedirectUri = (index: number) => {
		setRedirectUris(prev => prev.filter((_, i) => i !== index));
	};

	const toggleScope = (scope: string, checked: boolean) => {
		setScopes(prev => (checked ? [...prev, scope] : prev.filter(s => s !== scope)));
	};

	const save = () => {
		const request: ICreateOAuthClientRequest = {
			name: name.trim(),
			description: description.trim() || undefined,
			redirectUris: redirectUris.map(uri => uri.trim()).filter(Boolean),
			scopes: scopes.length > 0 ? scopes : undefined,
		};

		const validationError = validateCreateOAuthClient(request);
		if (validationError) {
			setError(validationError);
			return;
		}

		setError(null);
		createOAuthClient.mutate(request);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange} dismissOnOutsideClick={false}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Create OAuth Client</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<FieldGroup>
						{error ? (
							<span role='alert' className={css(styleSheet.error)}>
								{error}
							</span>
						) : null}

						<Field>
							<FieldLabel htmlFor='oauth-client-name'>Name</FieldLabel>
							<Input
								id='oauth-client-name'
								type='text'
								placeholder='My Integration'
								value={name}
								onChange={e => setName(e.target.value)}
							/>
						</Field>

						<Field>
							<FieldLabel htmlFor='oauth-client-description'>
								Description <span className={css(styleSheet.optionalHint)}>(optional)</span>
							</FieldLabel>
							<TextArea
								id='oauth-client-description'
								placeholder='What this client is used for'
								value={description}
								onChange={e => setDescription(e.target.value)}
							/>
						</Field>

						<Field>
							<FieldLabel>Redirect URIs</FieldLabel>
							{redirectUris.map((uri, index) => (
								// eslint-disable-next-line react/no-array-index-key
								<div key={`redirect-uri-${index}`} className={css(styleSheet.uriRow)}>
									<Input
										styles={[styleSheet.uriRowInput]}
										type='text'
										placeholder='https://example.com/callback'
										value={uri}
										onChange={e => updateRedirectUri(index, e.target.value)}
									/>
									{redirectUris.length > 1 ? (
										<Button
											variant='outline'
											size='sm'
											aria-label='Remove redirect URI'
											onClick={() => removeRedirectUri(index)}
										>
											Remove
										</Button>
									) : null}
								</div>
							))}
							{redirectUris.length < MAX_REDIRECT_URIS ? (
								<Button variant='link' styles={[styleSheet.addUriButton]} onClick={addRedirectUri}>
									+ Add redirect URI
								</Button>
							) : null}
						</Field>

						<Field>
							<FieldLabel>Scopes</FieldLabel>
							<div className={css(styleSheet.scopes)}>
								{PUBLIC_API_SCOPE_VALUES.map(scope => {
									const comingSoon = COMING_SOON_SCOPES.has(scope);
									return (
										<Label key={scope} styles={[comingSoon ? styleSheet.scopeDisabled : styleSheet.scopeOption]}>
											<Checkbox
												disabled={comingSoon}
												checked={!comingSoon && scopes.includes(scope)}
												onCheckedChange={checked => toggleScope(scope, checked === true)}
											/>
											{scope}
											{comingSoon ? <Pill variant='label' color='gray' label='Coming Soon' /> : null}
										</Label>
									);
								})}
							</div>
						</Field>
					</FieldGroup>
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' onClick={() => onOpenChange(false)} disabled={createOAuthClient.isPending}>
						Cancel
					</Button>
					<Button onClick={save} isLoading={createOAuthClient.isPending} disabled={createOAuthClient.isPending}>
						Create
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
